/**
 * End-Game Overlay - canonical UX for every game's win/loss resolution.
 *
 * Per `Vault/Design/Design Brief.md` "End-game overlay pattern (PM 2026-06-10)":
 *
 *   Modal Shell 400px mobile / 480px desktop.
 *   Top: full-bleed 56px colour bar (`--success` win, `--failure` loss),
 *        text white on colour, Geist semibold 22px, centred -
 *        "Solved!" or "Not solved today.".
 *   Stat panel: primary line + supporting lines (streak, time).
 *   Action stack (8px gap):
 *     Logged in:  Primary  "Share to Nostr" (Lucide `upload`)
 *                 Secondary "Copy text"     (Lucide `copy`)
 *                 Tertiary  "Back to puzzles"
 *     Anon:       Primary  "Sign in with Nostr to share"
 *                 Secondary "Copy text"
 *                 Tertiary  "Back to puzzles"
 *
 * PM 2026-06-10 bug pass (E/F/H/K):
 *   - The overlay now has a small close X on the colour bar, plus
 *     backdrop-tap and ESC dismissal (default; opt-out via `dismissible:
 *     false`). The tertiary "Back to puzzles" CTA still closes + routes.
 *   - When an anonymous user taps "Sign in with Nostr to share", the
 *     overlay tears itself down BEFORE invoking `onSignIn` so the Login
 *     Modal opens against a clean stage instead of stacking behind the
 *     result. Opt out via `autoDismissOnSignIn: false`.
 *   - The shell registers with the shared `modal-stack` at the `result`
 *     tier so the Login Modal (auth tier) always paints above it.
 *
 * Reuse: every game registers this overlay when its /finish returns.
 *
 * Colour cascade (PM 2026-06-11 polish pass):
 *   `outcome: 'win'`  -> hero + time row paint in --primary (brand blue).
 *   `outcome: 'loss'` -> hero + time row paint in --failure  (brand red).
 * The inline card (`end-game-inline-card.ts`) accepts the wider
 * `'won' | 'lost' | 'timed_out'` set and maps `'timed_out'` -> loss for the
 * same red treatment. New games (Anagram Sprint, Phase 14) MUST pass:
 *   - snapshot.outcome: 'won' on success, 'lost' on a strict fail, OR
 *     'timed_out' if the game has a sand-timer that expires. The shared
 *     controller derives modal `outcome` from snapshot.outcome via the
 *     existing `won -> win, * -> loss` map.
 *   - Games with no real fail state (Spelling Bee pattern) MUST pass
 *     `'won'` for every result so the colour cascade stays blue.
 */

import { iconSvg, type LucideName } from './icons';
import { registerModal, type ModalStackEntry } from './modal-stack';

export type EndGameOutcome = 'win' | 'loss';

export interface EndGameStats {
  /**
   * Top one-line headline summarising the run.
   * e.g. "4 of 4 groups in 5 picks. 1 mistake."
   */
  primary: string;
  /**
   * Supporting lines (streak, time-to-solve, etc). Rendered as <p>s under
   * the primary line. Pass an empty array if there is nothing extra.
   */
  lines: string[];
  /**
   * Optional hero line displayed prominently above `primary` in the brand
   * `--primary` colour at h2 size. Used for the marquee solve-time on games
   * that are timed (Connections, Worldle, etc).
   * PM 2026-06-10 bug G.
   * The `| undefined` is needed for exactOptionalPropertyTypes callers that
   * pass `hero: maybeUndefined` shorthand instead of conditionally spreading.
   */
  hero?: string | undefined;
  /**
   * Time-only row rendered ALONE on a line beneath the hero and above the
   * primary stat line. Format: `2m 45s` / `M:SS` / `H:MM:SS`. Coloured
   * `--primary` (blue) on a win, `--failure` (red) on a loss. Hidden when
   * omitted.
   * PM 2026-06-10 v3 (Pattern 1). PM 2026-06-11 polish pass bumped the
   * font size to 22px mobile / 26px desktop and swapped the loss colour
   * from `--muted-foreground` -> `--failure` to mirror the Connect4 V3
   * mockup the user approved.
   */
  time?: string | undefined;
  /**
   * Phase 19.5 v2 (PM 2026-06-12 Bowz brief): per-game raw score for THIS
   * solve. Surfaced as a "Score: N pts" row positioned above the rank
   * slot. The value is the same `gamePoints` the worker computes via
   * `recordSolveForFinish` (0..100ish per-game integer). Hidden when
   * undefined - games that do not run through the scoring engine
   * (anagram-sprint) and legacy snapshots leave this unset.
   */
  points?: number;
  /**
   * PM 2026-06-19 (Bowz brief): for par-using games (Word Ladder, Letter
   * Boxed, future golf-par titles), an optional context object describing
   * how this solve sat relative to par. When supplied, the shared
   * components paint the `Score: N pts` value GREEN (under par), BLUE (at
   * par) or RED (over par). Other games leave this unset; the score line
   * keeps the standard --foreground colour. Renderer is colour-only - no
   * extra UI is drawn from this prop.
   *
   *   parDelta = par - used  (positive = under par, 0 = at par, negative = over par)
   */
  parContext?: {
    par: number;
    used: number;
  };
  /**
   * Optional ranking slot rendered below `lines`. Shows a label
   * ("Rank: TBD" by default) and a small caption explaining when ranking
   * unlocks. Layout is real now so the slot can be verified; live wiring
   * lands with the leaderboard in Phase 19.
   * PM 2026-06-10 bug G.
   */
  rank?: {
    label: string;
    caption: string;
    /**
     * Phase 19 (PM 2026-06-11): when set, the rank block paints with a
     * medal tint via `data-medal`. `gold` for Rank 1, `silver` for 2,
     * `bronze` for 3.
     */
    medal?: 'gold' | 'silver' | 'bronze';
    /**
     * Optional hash route to navigate to when the rank pill is clicked.
     * Sets cursor:pointer + role=link affordance. When unset, rank pill
     * is non-interactive.
     */
    href?: string;
  };
  /**
   * Optional scrollable list of words the player found this round.
   * Spelling Bee (Phase 5) is the first consumer: NYT convention is to
   * show the tier + score, then a scrollable list of every word found
   * with pangrams highlighted. Hidden when omitted.
   * Rendered in DOM order; consumers should pre-sort.
   * PM 2026-06-11.
   */
  foundWords?: Array<{ word: string; isPangram?: boolean }>;
  /**
   * PM 2026-06-12 (Bowz brief): canonical pangrams for the round, rendered
   * as a separate "PANGRAMS" section above the found-words list (gold
   * heading + gold word chips). Optional - games that have no pangram
   * concept (every other word game) leave this unset.
   * Rendered in DOM order; consumers should pre-sort.
   */
  pangramsAll?: string[];
  /**
   * PM 2026-06-12 (Bowz brief): words the player MISSED (i.e. all valid
   * words minus foundWords). Rendered as a separate scrollable "WORDS
   * YOU MISSED" section beneath the found-words list, with muted-foreground
   * chips. Optional. Consumers should pre-sort.
   */
  missedWords?: string[];
  /**
   * PM 2026-06-12 (Bowz brief): total valid words today (foundWords.length +
   * missedWords.length). Used in the section headers to show "X / Y".
   * Optional - falls back to summing foundWords + missedWords lengths.
   */
  totalValidWords?: number;
}

export interface EndGameOverlayProps {
  outcome: EndGameOutcome;
  stats: EndGameStats;
  isLoggedIn: boolean;
  onShare: () => void | Promise<void>;
  onCopy: () => void | Promise<void>;
  onBackToPuzzles: () => void;
  /** Anonymous-only handler. Invoked from "Sign in with Nostr to share". */
  onSignIn?: () => void;
  /**
   * Caption for the very top colour bar. Defaults to:
   *  - "Solved!"            for win
   *  - "Not solved today."  for loss
   */
  bannerText?: string;
  /**
   * When true (default), the overlay can be dismissed via backdrop tap,
   * ESC, or the close X. Set to false for end-states where leaving the
   * overlay would orphan game state (rare; not currently used).
   * PM 2026-06-10 bug F.
   */
  dismissible?: boolean;
  /**
   * Invoked when the user dismisses via backdrop / ESC / close X (but NOT
   * via "Back to puzzles" or "Sign in" - those have their own callbacks).
   * Useful for games that want to render a "View result" pill on dismiss.
   */
  onDismiss?: () => void;
  /**
   * When true (default), anon users tapping "Sign in with Nostr to share"
   * cause the overlay to call its own teardown BEFORE invoking `onSignIn`.
   * This is the contract that prevents the Login Modal from opening behind
   * a stuck end-game overlay (PM 2026-06-10 bug E).
   */
  autoDismissOnSignIn?: boolean;
}

export interface EndGameOverlayHandle {
  /** Toggle the share button into a "Shared!" disabled state for `ms`. */
  flashShared: (ms?: number) => void;
  /** Programmatically swap props (rare; used by parents that re-render). */
  update: (props: EndGameOverlayProps) => void;
  /** Tear down. */
  destroy: () => void;
}

interface InternalRefs {
  backdrop: HTMLDivElement;
  shell: HTMLDivElement;
  banner: HTMLDivElement;
  bannerText: HTMLSpanElement;
  closeBtn: HTMLButtonElement;
  statHero: HTMLParagraphElement;
  statTime: HTMLParagraphElement;
  statPrimary: HTMLParagraphElement;
  statLines: HTMLDivElement;
  pointsBlock: HTMLDivElement;
  pointsValue: HTMLParagraphElement;
  rankBlock: HTMLDivElement;
  rankLabel: HTMLParagraphElement;
  rankCaption: HTMLParagraphElement;
  foundWordsBlock: HTMLDivElement;
  foundWordsHeader: HTMLParagraphElement;
  foundWordsList: HTMLUListElement;
  pangramsBlock: HTMLDivElement;
  pangramsHeader: HTMLParagraphElement;
  pangramsList: HTMLUListElement;
  missedWordsBlock: HTMLDivElement;
  missedWordsHeader: HTMLParagraphElement;
  missedWordsList: HTMLUListElement;
  shareBtn: HTMLButtonElement;
  shareBtnLabel: HTMLSpanElement;
  copyBtn: HTMLButtonElement;
  copyBtnLabel: HTMLSpanElement;
  backLink: HTMLButtonElement;
  anonFooter: HTMLParagraphElement;
}

function buildSkeleton(): InternalRefs {
  const backdrop = document.createElement('div');
  backdrop.className = 'gv-endgame-backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');

  const shell = document.createElement('div');
  shell.className = 'gv-endgame-shell';
  backdrop.appendChild(shell);

  const banner = document.createElement('div');
  banner.className = 'gv-endgame-banner';
  const bannerText = document.createElement('span');
  bannerText.className = 'gv-endgame-banner-text';
  banner.appendChild(bannerText);

  // Close X on the colour bar (PM 2026-06-10 bug F). Always painted in
  // white-on-tinted so it reads on both win + loss banners.
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'gv-endgame-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.appendChild(iconSvg('x', 18));
  banner.appendChild(closeBtn);

  shell.appendChild(banner);

  const body = document.createElement('div');
  body.className = 'gv-endgame-body';
  shell.appendChild(body);

  const statHero = document.createElement('p');
  statHero.className = 'gv-endgame-stat-hero';
  statHero.hidden = true;
  body.appendChild(statHero);

  // Time-only row (PM 2026-06-10 v3, Pattern 1; PM 2026-06-11 polish).
  // Mid-size, --primary (blue) on win, --failure (red) on loss / timed_out.
  // Hidden when `stats.time` is unset.
  const statTime = document.createElement('p');
  statTime.className = 'gv-endgame-stat-time';
  statTime.hidden = true;
  body.appendChild(statTime);

  const statPrimary = document.createElement('p');
  statPrimary.className = 'gv-endgame-stat-primary';
  body.appendChild(statPrimary);

  const statLines = document.createElement('div');
  statLines.className = 'gv-endgame-stat-lines';
  body.appendChild(statLines);

  // Phase 19.5 v2 (PM 2026-06-12 Bowz brief): per-game raw points row.
  // Rendered ABOVE the rank slot so the player sees "Score: 87 pts" the
  // moment they finish, before the rank caption explains where that sits
  // on the daily leaderboard. Hidden when `stats.points` is undefined.
  const pointsBlock = document.createElement('div');
  pointsBlock.className = 'gv-endgame-points';
  pointsBlock.hidden = true;
  const pointsValue = document.createElement('p');
  pointsValue.className = 'gv-endgame-points-value';
  pointsBlock.appendChild(pointsValue);
  body.appendChild(pointsBlock);

  // Optional ranking slot (PM 2026-06-10 bug G). Hidden by default; shown
  // when the consumer passes `stats.rank`.
  const rankBlock = document.createElement('div');
  rankBlock.className = 'gv-endgame-rank';
  rankBlock.hidden = true;
  const rankLabel = document.createElement('p');
  rankLabel.className = 'gv-endgame-rank-label';
  const rankCaption = document.createElement('p');
  rankCaption.className = 'gv-endgame-rank-caption';
  rankBlock.append(rankLabel, rankCaption);
  body.appendChild(rankBlock);

  // Pangrams block (PM 2026-06-12 Bowz brief). Rendered ABOVE the
  // found-words list when the consumer passes `stats.pangramsAll`. Header
  // styled in gold, chips styled in gold (matches the in-game found-words
  // card pangram tint). Spelling Bee is the only consumer today; other
  // games leave this unset and the block stays hidden.
  const pangramsBlock = document.createElement('div');
  pangramsBlock.className = 'gv-endgame-pangrams';
  pangramsBlock.hidden = true;
  const pangramsHeader = document.createElement('p');
  pangramsHeader.className = 'gv-endgame-pangrams-header';
  const pangramsList = document.createElement('ul');
  pangramsList.className = 'gv-endgame-pangrams-list';
  pangramsBlock.append(pangramsHeader, pangramsList);
  body.appendChild(pangramsBlock);

  // Found-words list (PM 2026-06-11). Optional scrollable list shown
  // beneath the rank block. Spelling Bee passes its sorted foundWords
  // here; pangrams get a `data-pangram="true"` marker that the shared
  // CSS paints gold. Hidden when `stats.foundWords` is unset so games
  // that have no equivalent concept (Connections, Worldle, ...) stay
  // unaffected.
  const foundWordsBlock = document.createElement('div');
  foundWordsBlock.className = 'gv-endgame-found-words';
  foundWordsBlock.hidden = true;
  const foundWordsHeader = document.createElement('p');
  foundWordsHeader.className = 'gv-endgame-found-words-header';
  const foundWordsList = document.createElement('ul');
  foundWordsList.className = 'gv-endgame-found-words-list';
  foundWordsBlock.append(foundWordsHeader, foundWordsList);
  body.appendChild(foundWordsBlock);

  // Missed-words block (PM 2026-06-12 Bowz brief). Optional scrollable
  // list of words the player did NOT find this round. Same shape as the
  // found-words block but rendered with `--muted-foreground` chips so
  // it reads as a secondary reveal.
  const missedWordsBlock = document.createElement('div');
  missedWordsBlock.className = 'gv-endgame-missed-words';
  missedWordsBlock.hidden = true;
  const missedWordsHeader = document.createElement('p');
  missedWordsHeader.className = 'gv-endgame-missed-words-header';
  const missedWordsList = document.createElement('ul');
  missedWordsList.className = 'gv-endgame-missed-words-list';
  missedWordsBlock.append(missedWordsHeader, missedWordsList);
  body.appendChild(missedWordsBlock);

  const actions = document.createElement('div');
  actions.className = 'gv-endgame-actions';
  body.appendChild(actions);

  const shareBtn = document.createElement('button');
  shareBtn.type = 'button';
  shareBtn.className = 'gv-endgame-btn gv-endgame-btn-primary';
  shareBtn.appendChild(iconSvg('upload', 18));
  const shareBtnLabel = document.createElement('span');
  shareBtn.appendChild(shareBtnLabel);
  actions.appendChild(shareBtn);

  const copyBtn = document.createElement('button');
  copyBtn.type = 'button';
  copyBtn.className = 'gv-endgame-btn gv-endgame-btn-secondary';
  copyBtn.appendChild(iconSvg('copy', 18));
  const copyBtnLabel = document.createElement('span');
  copyBtnLabel.textContent = 'Copy text';
  copyBtn.appendChild(copyBtnLabel);
  actions.appendChild(copyBtn);

  const backLink = document.createElement('button');
  backLink.type = 'button';
  backLink.className = 'gv-endgame-btn gv-endgame-btn-tertiary';
  backLink.textContent = 'Back to puzzles';
  actions.appendChild(backLink);

  const anonFooter = document.createElement('p');
  anonFooter.className = 'gv-endgame-anon-footer';
  anonFooter.textContent =
    'No account? Just play - your streaks save locally and you can sign in later.';
  body.appendChild(anonFooter);

  return {
    backdrop,
    shell,
    banner,
    bannerText,
    closeBtn,
    statHero,
    statTime,
    statPrimary,
    statLines,
    pointsBlock,
    pointsValue,
    rankBlock,
    rankLabel,
    rankCaption,
    foundWordsBlock,
    foundWordsHeader,
    foundWordsList,
    pangramsBlock,
    pangramsHeader,
    pangramsList,
    missedWordsBlock,
    missedWordsHeader,
    missedWordsList,
    shareBtn,
    shareBtnLabel,
    copyBtn,
    copyBtnLabel,
    backLink,
    anonFooter,
  };
}

function defaultBannerText(outcome: EndGameOutcome): string {
  return outcome === 'win' ? 'Solved!' : 'Not solved today.';
}

function shareLabel(isLoggedIn: boolean): string {
  return isLoggedIn ? 'Share to Nostr' : 'Sign in with Nostr to share';
}

function shareIcon(isLoggedIn: boolean): LucideName {
  return isLoggedIn ? 'upload' : 'log-in';
}

/**
 * PM 2026-06-19 (Bowz brief): set a `data-par-class` attribute on the
 * Score row so the shared stylesheet can paint the value GREEN / BLUE /
 * RED for under / at / over par. Games that don't pass `parContext`
 * clear the attribute and the value keeps the standard --foreground.
 *
 * Exported so the inline-card mirror can call the same helper.
 */
export function applyParClassAttr(
  el: HTMLElement,
  parContext: { par: number; used: number } | undefined,
): void {
  if (!parContext) {
    el.removeAttribute('data-par-class');
    return;
  }
  const delta = parContext.par - parContext.used;
  const klass = delta > 0 ? 'under' : delta === 0 ? 'at' : 'over';
  el.setAttribute('data-par-class', klass);
}

export function mountEndGameOverlay(
  target: HTMLElement,
  props: EndGameOverlayProps,
): EndGameOverlayHandle {
  const refs = buildSkeleton();
  let current = props;
  let flashTimer: number | null = null;
  let destroyed = false;
  let entry: ModalStackEntry | null = null;
  // Track rank-pill interactive listener so we can detach when the next
  // render no longer has an href (or on teardown). The listener is the
  // same fn for click + keydown so we only need one reference.
  let rankClickHandler: ((event: Event) => void) | null = null;
  let rankKeydownHandler: ((event: KeyboardEvent) => void) | null = null;

  function syncStatLines(lines: string[]): void {
    refs.statLines.replaceChildren();
    for (const l of lines) {
      const p = document.createElement('p');
      p.className = 'gv-endgame-stat-line';
      p.textContent = l;
      refs.statLines.appendChild(p);
    }
  }

  function syncShareIcon(loggedIn: boolean): void {
    const oldSvg = refs.shareBtn.querySelector('svg');
    if (oldSvg) oldSvg.remove();
    refs.shareBtn.insertBefore(iconSvg(shareIcon(loggedIn), 18), refs.shareBtnLabel);
  }

  function teardown(): void {
    if (destroyed) return;
    destroyed = true;
    refs.shareBtn.removeEventListener('click', onShareClick);
    refs.copyBtn.removeEventListener('click', onCopyClick);
    refs.backLink.removeEventListener('click', onBackClick);
    refs.closeBtn.removeEventListener('click', onCloseClick);
    refs.backdrop.removeEventListener('click', onBackdropClick);
    if (rankClickHandler) {
      refs.rankBlock.removeEventListener('click', rankClickHandler);
      rankClickHandler = null;
    }
    if (rankKeydownHandler) {
      refs.rankBlock.removeEventListener('keydown', rankKeydownHandler);
      rankKeydownHandler = null;
    }
    if (flashTimer !== null) {
      window.clearTimeout(flashTimer);
      flashTimer = null;
    }
    entry?.release();
    refs.backdrop.remove();
  }

  function onShareClick(): void {
    if (current.isLoggedIn) {
      void current.onShare();
      return;
    }
    if (current.onSignIn) {
      // PM 2026-06-10 bug E: dismiss FIRST so the Login Modal opens against
      // a clean stack (no result hiding behind it), then invoke onSignIn.
      const handler = current.onSignIn;
      if (current.autoDismissOnSignIn ?? true) {
        teardown();
      }
      handler();
    }
  }
  function onCopyClick(): void {
    void current.onCopy();
    refs.copyBtnLabel.textContent = 'Copied';
    window.setTimeout(() => {
      if (!destroyed) refs.copyBtnLabel.textContent = 'Copy text';
    }, 1500);
  }
  function onBackClick(): void {
    current.onBackToPuzzles();
  }
  function onCloseClick(): void {
    handleDismiss();
  }
  function onBackdropClick(e: MouseEvent): void {
    if (e.target !== refs.backdrop) return;
    if (!(current.dismissible ?? true)) return;
    handleDismiss();
  }
  function handleDismiss(): void {
    // Capture the optional callback before teardown nulls it out.
    const dismissCb = current.onDismiss;
    teardown();
    dismissCb?.();
  }

  refs.shareBtn.addEventListener('click', onShareClick);
  refs.copyBtn.addEventListener('click', onCopyClick);
  refs.backLink.addEventListener('click', onBackClick);
  refs.closeBtn.addEventListener('click', onCloseClick);
  refs.backdrop.addEventListener('click', onBackdropClick);

  function render(p: EndGameOverlayProps): void {
    current = p;
    refs.backdrop.setAttribute('data-outcome', p.outcome);
    refs.shell.setAttribute('data-outcome', p.outcome);
    refs.bannerText.textContent = p.bannerText ?? defaultBannerText(p.outcome);
    if (p.stats.hero) {
      refs.statHero.textContent = p.stats.hero;
      refs.statHero.setAttribute('data-outcome', p.outcome);
      refs.statHero.hidden = false;
    } else {
      refs.statHero.textContent = '';
      refs.statHero.removeAttribute('data-outcome');
      refs.statHero.hidden = true;
    }
    // Time-only row beneath the hero (PM 2026-06-10 v3, Pattern 1;
    // PM 2026-06-11 polish). The `data-outcome` attribute drives the
    // colour cascade: --primary (blue) on win, --failure (red) on loss.
    if (p.stats.time) {
      refs.statTime.textContent = p.stats.time;
      refs.statTime.setAttribute('data-outcome', p.outcome);
      refs.statTime.hidden = false;
    } else {
      refs.statTime.textContent = '';
      refs.statTime.hidden = true;
    }
    refs.statPrimary.textContent = p.stats.primary;
    syncStatLines(p.stats.lines);
    // Phase 19.5 v2 (PM 2026-06-12 Bowz brief): per-game raw points row.
    // Render as "Score: N pts" when `stats.points` is a non-negative
    // number. Hidden otherwise (legacy snapshots, anon plays where the
    // worker did not emit a score, or games not yet wired).
    if (typeof p.stats.points === 'number' && Number.isFinite(p.stats.points) && p.stats.points >= 0) {
      refs.pointsValue.textContent = `Score: ${p.stats.points} pts`;
      refs.pointsBlock.hidden = false;
    } else {
      refs.pointsValue.textContent = '';
      refs.pointsBlock.hidden = true;
    }
    // PM 2026-06-19 (Bowz brief): par-aware scorecard colour. For golf-par
    // games the consumer supplies `parContext` and the points value paints
    // GREEN (under par), BLUE (at par), or RED (over par). Other games
    // omit `parContext` and the value keeps the standard --foreground.
    applyParClassAttr(refs.pointsValue, p.stats.parContext);
    if (p.stats.rank) {
      refs.rankLabel.textContent = p.stats.rank.label;
      refs.rankCaption.textContent = p.stats.rank.caption;
      if (p.stats.rank.medal) {
        refs.rankBlock.setAttribute('data-medal', p.stats.rank.medal);
      } else {
        refs.rankBlock.removeAttribute('data-medal');
      }
      refs.rankBlock.hidden = false;
      // Clickable rank pill: when the consumer supplies a hash route, wire
      // role=link + tabindex + click/keydown listeners that navigate via
      // `window.location.hash`. When no href, ensure prior interactive
      // attributes/listeners are stripped (re-renders with a different
      // shape must not leave stale state).
      const href = p.stats.rank.href;
      if (href) {
        refs.rankBlock.setAttribute('role', 'link');
        refs.rankBlock.setAttribute('tabindex', '0');
        refs.rankBlock.dataset.interactive = 'true';
        if (rankClickHandler) {
          refs.rankBlock.removeEventListener('click', rankClickHandler);
        }
        if (rankKeydownHandler) {
          refs.rankBlock.removeEventListener('keydown', rankKeydownHandler);
        }
        rankClickHandler = (event: Event) => {
          event.stopPropagation();
          window.location.hash = href;
        };
        rankKeydownHandler = (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
            event.preventDefault();
            event.stopPropagation();
            window.location.hash = href;
          }
        };
        refs.rankBlock.addEventListener('click', rankClickHandler);
        refs.rankBlock.addEventListener('keydown', rankKeydownHandler);
      } else {
        refs.rankBlock.removeAttribute('role');
        refs.rankBlock.removeAttribute('tabindex');
        delete refs.rankBlock.dataset.interactive;
        if (rankClickHandler) {
          refs.rankBlock.removeEventListener('click', rankClickHandler);
          rankClickHandler = null;
        }
        if (rankKeydownHandler) {
          refs.rankBlock.removeEventListener('keydown', rankKeydownHandler);
          rankKeydownHandler = null;
        }
      }
    } else {
      refs.rankBlock.hidden = true;
      refs.rankBlock.removeAttribute('data-medal');
      refs.rankBlock.removeAttribute('role');
      refs.rankBlock.removeAttribute('tabindex');
      delete refs.rankBlock.dataset.interactive;
      if (rankClickHandler) {
        refs.rankBlock.removeEventListener('click', rankClickHandler);
        rankClickHandler = null;
      }
      if (rankKeydownHandler) {
        refs.rankBlock.removeEventListener('keydown', rankKeydownHandler);
        rankKeydownHandler = null;
      }
    }
    // Pangrams block (PM 2026-06-12 Bowz brief). Rendered ABOVE the
    // found-words list when populated. Spelling Bee passes its canonical
    // pangrams here regardless of whether the player found them, so the
    // section always shows the day's pangrams as a reveal.
    const pg = p.stats.pangramsAll;
    if (pg && pg.length > 0) {
      refs.pangramsHeader.textContent = `Pangram${pg.length === 1 ? '' : 's'}`;
      refs.pangramsList.replaceChildren();
      for (const word of pg) {
        const li = document.createElement('li');
        li.className = 'gv-endgame-pangram-chip';
        li.textContent = word.toUpperCase();
        refs.pangramsList.appendChild(li);
      }
      refs.pangramsBlock.hidden = false;
    } else {
      refs.pangramsBlock.hidden = true;
      refs.pangramsList.replaceChildren();
    }
    // Found-words list (PM 2026-06-11). Render only when populated.
    const fw = p.stats.foundWords;
    if (fw && fw.length > 0) {
      const total =
        typeof p.stats.totalValidWords === 'number'
          ? p.stats.totalValidWords
          : fw.length + (p.stats.missedWords?.length ?? 0);
      refs.foundWordsHeader.textContent =
        total > fw.length
          ? `Words you found (${fw.length} / ${total})`
          : `Words you found (${fw.length})`;
      refs.foundWordsList.replaceChildren();
      for (const entry of fw) {
        const li = document.createElement('li');
        li.className = 'gv-endgame-found-word';
        if (entry.isPangram) li.setAttribute('data-pangram', 'true');
        li.textContent = entry.word.toUpperCase();
        refs.foundWordsList.appendChild(li);
      }
      refs.foundWordsBlock.hidden = false;
    } else {
      refs.foundWordsBlock.hidden = true;
      refs.foundWordsList.replaceChildren();
    }
    // Missed-words block (PM 2026-06-12 Bowz brief). Renders BELOW the
    // found-words list when populated.
    const mw = p.stats.missedWords;
    if (mw && mw.length > 0) {
      refs.missedWordsHeader.textContent = `Words you missed (${mw.length})`;
      refs.missedWordsList.replaceChildren();
      for (const word of mw) {
        const li = document.createElement('li');
        li.className = 'gv-endgame-missed-word';
        li.textContent = word.toUpperCase();
        refs.missedWordsList.appendChild(li);
      }
      refs.missedWordsBlock.hidden = false;
    } else {
      refs.missedWordsBlock.hidden = true;
      refs.missedWordsList.replaceChildren();
    }
    refs.shareBtnLabel.textContent = shareLabel(p.isLoggedIn);
    syncShareIcon(p.isLoggedIn);
    refs.anonFooter.hidden = p.isLoggedIn;
    const dismissible = p.dismissible ?? true;
    refs.closeBtn.hidden = !dismissible;
  }

  render(props);
  target.appendChild(refs.backdrop);

  const dismissible = props.dismissible ?? true;
  entry = registerModal({
    element: refs.backdrop,
    tier: 'result',
    onClose: (): void => {
      // Routed here from the global ESC handler. Only act if dismissible.
      if (!(current.dismissible ?? true)) return;
      handleDismiss();
    },
    dismissOnEsc: dismissible,
    dismissOnBackdrop: dismissible,
  });

  return {
    flashShared(ms: number = 2000): void {
      if (destroyed) return;
      refs.shareBtn.disabled = true;
      const original = refs.shareBtnLabel.textContent ?? '';
      refs.shareBtnLabel.textContent = 'Shared!';
      if (flashTimer !== null) {
        window.clearTimeout(flashTimer);
      }
      flashTimer = window.setTimeout(() => {
        if (destroyed) return;
        refs.shareBtn.disabled = false;
        refs.shareBtnLabel.textContent = original;
        flashTimer = null;
      }, ms);
    },
    update(next: EndGameOverlayProps): void {
      render(next);
    },
    destroy(): void {
      teardown();
    },
  };
}

/**
 * Phase 19 (PM 2026-06-11): derive the end-game rank slot from the worker's
 * `/finish` response. Centralised so all 15 game.ts files use the same copy.
 *
 *   - `dailyRank === 1`        -> "Rank 1" + first-solve caption + gold medal
 *   - `dailyRank` 2/3          -> "Rank N" + silver/bronze medal
 *   - `dailyRank` >= 4         -> "Rank N" + neutral chip, optional total in caption
 *   - `isLoggedIn === false`   -> "Sign in to rank" + sign-in caption
 *   - `dailyRank == null/undefined` (logged in, no score yet) -> "Rank: TBD"
 *
 * The caller passes the parsed `/finish` response's `dailyRank` and
 * `dailyTotal` fields plus the auth state; this returns the `{label,
 * caption, medal}` shape the EndGameOverlay's rank slot expects.
 */
export function formatRankSlot(
  dailyRank: number | null | undefined,
  dailyTotal: number | undefined,
  isLoggedIn: boolean,
): {
  label: string;
  caption: string;
  medal?: 'gold' | 'silver' | 'bronze';
} {
  if (!isLoggedIn) {
    return {
      label: 'Sign in to rank',
      caption: 'Anonymous plays count for your local streak but not the leaderboard.',
    };
  }
  if (typeof dailyRank !== 'number' || dailyRank <= 0) {
    return {
      label: 'Rank: TBD',
      caption: 'Ranking unlocks when the leaderboard finishes computing.',
    };
  }
  const totalLine =
    typeof dailyTotal === 'number'
      ? `${dailyTotal} points today.`
      : 'Tap to view the daily leaderboard.';
  if (dailyRank === 1) {
    return {
      label: 'Rank 1',
      caption: `First solve today. ${totalLine}`,
      medal: 'gold',
    };
  }
  if (dailyRank === 2) {
    return { label: 'Rank 2', caption: totalLine, medal: 'silver' };
  }
  if (dailyRank === 3) {
    return { label: 'Rank 3', caption: totalLine, medal: 'bronze' };
  }
  return { label: `Rank ${dailyRank}`, caption: totalLine };
}

/**
 * Phase 19: short, tile-friendly rank label used by the homepage tile
 * completion overlay. Returns:
 *   - "Rank 1" for top, "Rank 14" for others
 *   - "Sign in to rank" for anonymous
 *   - "Rank: TBD" while ranking is unresolved (logged-in placeholder)
 */
export function formatTileRankLabel(
  dailyRank: number | null | undefined,
  isLoggedIn: boolean,
): string {
  if (!isLoggedIn) return 'Sign in to rank';
  if (typeof dailyRank !== 'number' || dailyRank <= 0) return 'Rank: TBD';
  return `Rank ${dailyRank}`;
}
